<script setup lang="ts">
import {
  Animations,
  Categorey,
  Direction,
  Sex,
} from "@/enums/characterGeneratorEnums";
import { SelectedDefinition, SelectedItems } from "@/types/sheet-definiation";
import { useImageInstance, useSpriteSheetUrl } from "@/use/common";
import GIF from "gif.js";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    selectedSex: Sex;
    selectedItems: SelectedItems;
    selectedAnimation: Animations;
    direction: Direction;
    isPlaying?: boolean;
    previewColumn?: number;
  }>(),
  {
    isPlaying: true,
    previewColumn: 1,
  }
);

// 动画相关常量
const FRAME_SIZE = 64;
const FRAME_DURATION = 150; // 每帧持续时间，单位毫秒

// Canvas引用
const canvasRef = ref<HTMLCanvasElement | null>(null);

// 动画状态
const animationState = ref({
  frameId: 0,
  currentFrame: 1,
  lastFrameTime: 0,
});

// 加载状态
const isAllImagesLoaded = ref(false);
const loadedImages = ref<Record<string, HTMLImageElement>>({});

// 获取当前动画的帧数
const animationFrameCounts: Record<string, number> = {
  spellcast: 7,
  thrust: 8,
  walk: 9,
  slash: 6,
  shoot: 13,
  hurt: 6,
  climb: 6,
  idle: 2,
  jump: 5,
  sit: 3,
  emote: 3,
  run: 8,
  combat_idle: 2,
  backslash: 13,
  halfslash: 7,
};

// 获取当前动画的帧数
const currentAnimationFrames = computed(() => {
  const animation = props.selectedAnimation.toLowerCase();
  return animationFrameCounts[animation] || 8; // 默认8帧
});

// 从新的数据结构中提取所有已选择的项目
const itemsList = computed(() => {
  const items: SelectedDefinition[] = [];

  // 遍历所有类别
  for (const category in props.selectedItems) {
    const categoryItems = props.selectedItems[category as Categorey];

    // 遍历每个类别中的项目
    for (const key in categoryItems) {
      items.push(categoryItems[key]);
    }
  }

  return items;
});

// 根据zPos排序的图层URL列表
const sortedLayers = computed(() => {
  const result: Array<{
    url: string;
    zPos: number;
    variant: string;
    definition: SelectedDefinition;
  }> = [];

  for (const item of itemsList.value) {
    for (const key of Object.keys(item)) {
      if (key.includes("layer_")) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const layer = item[key] as any; // 临时使用any绕过类型检查
        const sexKey = props.selectedSex;

        if (
          layer &&
          typeof layer === "object" &&
          "zPos" in layer &&
          sexKey in layer
        ) {
          const url = layer[sexKey]
            .replace("${head}", props.selectedSex)
            .replace("${expression}", "default");
          if (typeof url === "string") {
            // 使用selectedVariant作为变体索引
            const variantIndex =
              typeof item.selectedVariant === "number"
                ? item.selectedVariant
                : 0;

            result.push({
              url,
              zPos: layer.zPos,
              variant: item.variants[variantIndex],
              definition: item,
            });
          }
        }
      }
    }
  }

  // 根据zPos排序
  return result.sort((a, b) => a.zPos - b.zPos);
});

// 停止动画
const stopAnimation = () => {
  if (animationState.value.frameId) {
    cancelAnimationFrame(animationState.value.frameId);
    animationState.value.frameId = 0;
  }
};

// 定义事件
const emit = defineEmits<{
  "update:canvasRef": [HTMLCanvasElement];
  "update:imageLoaded": [boolean];
}>();

// 预加载所有图片
const preloadImages = async () => {
  isAllImagesLoaded.value = false;
  loadedImages.value = {};

  // 为每一个图层创建图片对象
  const loadPromises = sortedLayers.value.map((layer) => {
    return new Promise<void>((resolve) => {
      const image = useImageInstance();
      const url = useSpriteSheetUrl(
        layer.url,
        props.selectedAnimation,
        layer.variant
      );
      image.src = url;

      image.onload = () => {
        loadedImages.value[url] = image;
        resolve();
      };

      image.onerror = () => {
        // console.error(`Failed to load image: ${url}`);
        resolve(); // 即使出错也继续
      };
    });
  });

  await Promise.all(loadPromises);
  isAllImagesLoaded.value = true;
  emit("update:imageLoaded", true);
};

// 绘制单帧
const drawFrame = () => {
  const canvas = canvasRef.value;
  if (!canvas) {
    console.warn(`Canvas not found`);
    return;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    console.warn(`Failed to get context`);
    return;
  }

  // 清空画布
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // 绘制每个图层
  for (const layer of sortedLayers.value) {
    const url = useSpriteSheetUrl(
      layer.url,
      props.selectedAnimation,
      layer.variant
    );
    const image = loadedImages.value[url];
    const definition = layer.definition;

    if (image) {
      // 使用预览行列和偏移量配置
      const column =
        definition.preview_column ??
        (props.isPlaying
          ? animationState.value.currentFrame
          : props.previewColumn);
      const row = definition.preview_row ?? props.direction;
      const preview_x_offset = definition.preview_x_offset ?? 0;
      const preview_y_offset = definition.preview_y_offset ?? 0;

      // 计算源图像的位置
      const sourceX = column * FRAME_SIZE + preview_x_offset;
      const sourceY = row * FRAME_SIZE + preview_y_offset;

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        FRAME_SIZE,
        FRAME_SIZE,
        0,
        0,
        FRAME_SIZE,
        FRAME_SIZE
      );
    } else {
      console.warn(`Image not loaded for URL: ${url}`);
    }
  }
};

// 执行动画循环
const animate = (timestamp: number) => {
  if (!isAllImagesLoaded.value || !props.isPlaying) {
    animationState.value.frameId = requestAnimationFrame(animate);
    return;
  }

  if (!animationState.value.lastFrameTime)
    animationState.value.lastFrameTime = timestamp;
  const deltaTime = timestamp - animationState.value.lastFrameTime;

  if (deltaTime >= FRAME_DURATION) {
    if (animationState.value.currentFrame === 0) {
      animationState.value.currentFrame = 1;
    } else {
      animationState.value.currentFrame =
        (animationState.value.currentFrame + 1) % currentAnimationFrames.value;
      if (animationState.value.currentFrame === 0) {
        animationState.value.currentFrame = 1;
      }
    }

    animationState.value.lastFrameTime = timestamp;
    drawFrame();
  }

  if (!canvasRef.value) {
    return;
  }

  animationState.value.frameId = requestAnimationFrame(animate);
};

// 重新渲染画布
const rerender = async () => {
  stopAnimation();
  await preloadImages();

  // 重置动画状态
  animationState.value.currentFrame = 1;
  animationState.value.lastFrameTime = 0;

  // 如果不是播放模式，则直接绘制静态帧
  if (!props.isPlaying) {
    drawFrame();
    return;
  }

  // 启动动画
  animationState.value.frameId = requestAnimationFrame(animate);
};

// 组件挂载和监听属性变化
onMounted(async () => {
  await rerender();
});

// 监听属性变化
watch(
  [
    () => props.selectedAnimation,
    () => props.selectedSex,
    () => props.selectedItems,
    () => props.direction,
    () => props.isPlaying,
    () => props.previewColumn,
  ],
  async () => {
    await rerender();
  }
);

// 监听canvasRef的变化
watch(
  canvasRef,
  (newCanvas) => {
    if (newCanvas) {
      emit("update:canvasRef", newCanvas);
    }
  },
  { immediate: true }
);

// 组件销毁时清理
onUnmounted(() => {
  stopAnimation();
});

// 提供绘制特定帧的方法给父组件
const drawSpecificFrame = (frame: number) => {
  const canvas = canvasRef.value;
  if (!canvas) {
    console.warn(`Canvas not found`);
    return null;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    console.warn(`Failed to get context`);
    return null;
  }

  // 清空画布
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // 绘制每个图层
  for (const layer of sortedLayers.value) {
    const url = useSpriteSheetUrl(
      layer.url,
      props.selectedAnimation,
      layer.variant
    );
    const image = loadedImages.value[url];
    const definition = layer.definition;

    if (image) {
      // 使用预览行列和偏移量配置
      const column = definition.preview_column ?? frame;
      const row = definition.preview_row ?? props.direction;
      const preview_x_offset = definition.preview_x_offset ?? 0;
      const preview_y_offset = definition.preview_y_offset ?? 0;

      // 计算源图像的位置
      const sourceX = column * FRAME_SIZE + preview_x_offset;
      const sourceY = row * FRAME_SIZE + preview_y_offset;

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        FRAME_SIZE,
        FRAME_SIZE,
        0,
        0,
        FRAME_SIZE,
        FRAME_SIZE
      );
    }
  }

  return canvas;
};

// GIF导出相关
const isExporting = ref(false);
const exportProgress = ref(0);

// 导出GIF方法
const exportToGif = (
  options: {
    onComplete?: (blob: Blob, url: string) => void;
    autoDownload?: boolean;
    filename?: string;
  } = { autoDownload: false }
) => {
  if (!isAllImagesLoaded.value) {
    console.warn("Images not loaded yet");
    return;
  }

  isExporting.value = true;
  exportProgress.value = 0;

  // 创建GIF编码器
  const gif = new GIF({
    workers: 2,
    quality: 10,
    width: FRAME_SIZE,
    height: FRAME_SIZE,
    workerScript: "/gif.worker.js",
    transparent: "rgba(0,0,0,0)",
    background: "rgba(0,0,0,0)",
  });

  // 添加进度事件
  gif.on("progress", (p) => {
    exportProgress.value = Math.round(p * 100);
  });

  // 当GIF生成完成时触发
  gif.on("finished", (blob) => {
    // 创建URL
    const url = URL.createObjectURL(blob);

    // 如果提供了回调函数，调用它
    if (options.onComplete) {
      options.onComplete(blob, url);
    }

    // 如果需要自动下载
    if (options.autoDownload) {
      const a = document.createElement("a");
      a.href = url;
      const filename =
        options.filename ||
        `character_animation_${Direction[props.direction]}.gif`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    // 如果没有提供回调函数且不需要自动下载，则释放URL
    if (!options.onComplete && !options.autoDownload) {
      URL.revokeObjectURL(url);
    }

    isExporting.value = false;
    exportProgress.value = 0;
  });

  // 获取当前动画的帧数
  const totalFrames = currentAnimationFrames.value;

  // 从第1帧开始（跳过第0帧，通常是空白帧）
  for (let frame = 1; frame < totalFrames; frame++) {
    const frameCanvas = drawSpecificFrame(frame);

    if (frameCanvas) {
      // 将当前帧添加到GIF
      gif.addFrame(frameCanvas, { delay: FRAME_DURATION, copy: true });
    }
  }

  // 开始渲染GIF
  gif.render();
};

// 导出给父组件使用的方法
defineExpose({
  drawSpecificFrame,
  exportToGif,
});
</script>

<template>
  <div class="flex flex-col items-center justify-center">
    <canvas
      ref="canvasRef"
      :width="FRAME_SIZE"
      :height="FRAME_SIZE"
      class="w-full aspect-square character-canvas"
    />
    <div v-if="!isAllImagesLoaded" class="loading-indicator">加载中...</div>

    <!-- 导出进度指示器 -->
    <div v-if="isExporting" class="export-progress">
      <div class="progress-text">导出GIF中: {{ exportProgress }}%</div>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: `${exportProgress}%` }"
        ></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.character-canvas {
  image-rendering: pixelated;
  width: 100%;
  height: 100%;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #666;
}

.export-progress {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  z-index: 10;
}

.progress-text {
  margin-bottom: 8px;
}

.progress-bar {
  width: 80%;
  height: 10px;
  background-color: #333;
  border-radius: 5px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s ease;
}
</style>
